import { Check, Copy } from "@phosphor-icons/react";
import { forwardRef, useEffect, useRef, useState, type HTMLAttributes, type MouseEvent, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Icon } from "../../primitives/Icon";

// Reveal affordance icon — matches the reference application's eye glyph exactly.
const EyeGlyph = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M7 4.5c1.38 0 2.5 1.12 2.5 2.5S8.38 9.5 7 9.5 4.5 8.38 4.5 7 5.62 4.5 7 4.5zm0-2C3.89 2.5 1.26 4.61.5 7c.76 2.39 3.39 4.5 6.5 4.5s5.74-2.11 6.5-4.5c-.76-2.39-3.39-4.5-6.5-4.5z"
      fill="currentColor"
    />
  </svg>
);
import { Text } from "../../primitives/Text";
import { IconButton } from "../../actions/IconButton";

export type RevealAndCopySize = "xs" | "sm" | "md";

export type RevealAndCopyProps = Omit<HTMLAttributes<HTMLDivElement>, "children" | "onCopy"> & {
  /** The sensitive value to keep masked until the user reveals it. */
  value?: string | null;
  /**
   * Optional formatted/display representation rendered ONLY when revealed (e.g. a
   * formatted phone number). The mask length still derives from `value`, and the
   * plaintext `displayValue` is never present in the DOM while masked. When
   * omitted, the revealed value falls back to `value` (unchanged behavior).
   */
  displayValue?: ReactNode;
  /** Enables the copy action after reveal. */
  allowCopy?: boolean;
  /** Initial reveal state for uncontrolled usage. */
  defaultRevealed?: boolean;
  /** Controlled reveal state. */
  revealed?: boolean;
  /** Called when the user reveals the value. */
  onRevealChange?: (revealed: boolean) => void;
  /** Called after a successful copy action. */
  onCopy?: (value: string) => void;
  /** Called when clipboard copy fails. */
  onCopyError?: (error: unknown) => void;
  /** Value written to the clipboard. Defaults to `value`. */
  copyValue?: string;
  /** Visible fallback for missing values. */
  emptyValue?: ReactNode;
  /** Character used to build the masked value. */
  maskCharacter?: string;
  /** Minimum masked length. */
  minMaskLength?: number;
  /** Stops button clicks from bubbling to parent rows/cards. */
  stopPropagation?: boolean;
  size?: RevealAndCopySize;
  revealLabel?: string;
  copyLabel?: string;
  copiedLabel?: string;
  valueClassName?: string;
  actionClassName?: string;
};

const textSizeClassName: Record<RevealAndCopySize, string> = {
  xs: "bfrs-text-[11px]",
  sm: "bfrs-text-[12px]",
  md: "bfrs-text-sm"
};

const iconSize: Record<RevealAndCopySize, number> = {
  xs: 12,
  sm: 14,
  md: 16
};

const actionSizeClassName: Record<RevealAndCopySize, string> = {
  xs: "bfrs-h-3.5 bfrs-w-3.5",
  sm: "bfrs-h-[14px] bfrs-w-[14px]",
  md: "bfrs-h-6 bfrs-w-6"
};

const copyResetDelayMs = 1500;

const isMissingValue = (value?: string | null) => {
  if (value === undefined || value === null) {
    return true;
  }

  const trimmed = value.trim();
  return trimmed.length === 0 || trimmed === "—";
};

const createMask = (value: string, maskCharacter: string, minMaskLength: number) => {
  const character = maskCharacter.length > 0 ? Array.from(maskCharacter)[0] : "X";
  return character.repeat(Math.max(value.length, minMaskLength));
};

export const RevealAndCopy = forwardRef<HTMLDivElement, RevealAndCopyProps>(
  (
    {
      className,
      value,
      allowCopy = true,
      defaultRevealed = false,
      revealed,
      onRevealChange,
      onCopy,
      onCopyError,
      copyValue,
      displayValue,
      emptyValue = "—",
      maskCharacter = "X",
      minMaskLength = 4,
      stopPropagation = true,
      size = "sm",
      revealLabel = "Show value",
      copyLabel = "Copy value",
      copiedLabel = "Copied",
      valueClassName,
      actionClassName,
      ...props
    },
    ref
  ) => {
    const [internalRevealed, setInternalRevealed] = useState(defaultRevealed);
    const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
    const timeoutRef = useRef<number | undefined>();
    const hasValue = !isMissingValue(value);
    const normalizedValue = value ?? "";
    const isRevealed = revealed ?? internalRevealed;
    // Mask length always derives from `value`. When revealed, prefer the optional
    // `displayValue` (formatted) and fall back to the raw value. While masked, only
    // the mask is rendered — `displayValue` is never placed in the DOM.
    const shownValue = isRevealed
      ? displayValue ?? normalizedValue
      : createMask(normalizedValue, maskCharacter, minMaskLength);

    useEffect(() => {
      return () => {
        if (timeoutRef.current !== undefined) {
          window.clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    const revealValue = (event: MouseEvent<HTMLButtonElement>) => {
      if (stopPropagation) {
        event.stopPropagation();
      }

      if (revealed === undefined) {
        setInternalRevealed(true);
      }

      onRevealChange?.(true);
    };

    const handleCopy = async (event: MouseEvent<HTMLButtonElement>) => {
      if (stopPropagation) {
        event.stopPropagation();
      }

      const textToCopy = copyValue ?? normalizedValue;

      try {
        await globalThis.navigator.clipboard.writeText(textToCopy);
        setCopyState("copied");
        onCopy?.(textToCopy);

        if (timeoutRef.current !== undefined) {
          window.clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = window.setTimeout(() => setCopyState("idle"), copyResetDelayMs);
      } catch (error) {
        onCopyError?.(error);
      }
    };

    if (!hasValue) {
      return (
        <div ref={ref} className={cn("bfrs-inline-flex bfrs-min-w-0 bfrs-items-center bfrs-align-middle", className)} {...props}>
          <Text as="span" tone="muted" className={cn(textSizeClassName[size], valueClassName)}>
            {emptyValue}
          </Text>
        </div>
      );
    }

    return (
      <div ref={ref} className={cn("bfrs-inline-flex bfrs-min-w-0 bfrs-items-center bfrs-gap-1.5 bfrs-align-middle", className)} {...props}>
        <Text as="span" className={cn("bfrs-min-w-0 bfrs-font-medium bfrs-leading-none bfrs-text-text-primary", textSizeClassName[size], valueClassName)}>
          {shownValue}
        </Text>
        {!isRevealed && (
          <IconButton
            type="button"
            aria-label={revealLabel}
            title={revealLabel}
            variant="ghost"
            size="sm"
            className={cn(actionSizeClassName[size], "bfrs-rounded-sm bfrs-p-0 bfrs-text-text-secondary hover:bfrs-bg-transparent hover:bfrs-text-text-primary", actionClassName)}
            icon={<EyeGlyph size={iconSize[size]} />}
            onClick={revealValue}
          />
        )}
        {isRevealed && allowCopy && (
          <>
            <IconButton
              type="button"
              aria-label={copyState === "copied" ? copiedLabel : copyLabel}
              title={copyState === "copied" ? copiedLabel : copyLabel}
              variant="ghost"
              size="sm"
              className={cn(
                actionSizeClassName[size],
                "bfrs-rounded-sm bfrs-p-0 hover:bfrs-bg-transparent",
                copyState === "copied" ? "bfrs-text-success" : "bfrs-text-text-secondary hover:bfrs-text-text-primary",
                actionClassName
              )}
              icon={<Icon icon={copyState === "copied" ? Check : Copy} size={iconSize[size]} weight="bold" />}
              onClick={handleCopy}
            />
            <span className="bfrs-sr-only" aria-live="polite">
              {copyState === "copied" ? copiedLabel : ""}
            </span>
          </>
        )}
      </div>
    );
  }
);

RevealAndCopy.displayName = "RevealAndCopy";
